import React, { useMemo, useState } from 'react';
import { FilterTabs } from '../ui/FilterTabs';
import { FilterSection } from '../ui/FilterSection';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonRow } from '../ui/Skeleton';
import { statusLabel, LOSS_REASON_LABEL, HOLD_REASON_LABEL, formatShortActivityDate } from './workflowConstants';

function isShopFloorEvent(type) {
  if (!type) return false;
  if (type === 'VENDOR_SEND' || type === 'VENDOR_RECEIVE') return true;
  return String(type).startsWith('BEAM');
}

function parseContext(json) {
  if (!json) return {};
  try {
    const o = JSON.parse(json);
    return o && typeof o === 'object' ? o : {};
  } catch {
    return {};
  }
}

function workflowStatusLabel(s) {
  if (!s) return '—';
  const raw = String(s).trim();
  const label = statusLabel(raw);
  if (label !== raw) return label;
  return humanizeSnake(raw);
}

function humanizeSnake(s) {
  return String(s)
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

/**
 * One compact line of facts (no raw JSON). Date + user stay in the card header.
 */
function activityMetaLine(a, canSeeCommercial = true) {
  const ctx = parseContext(a.contextJson);
  const parts = [];

  switch (a.eventType) {
    case 'BEAM_START':
    case 'BEAM_HOLD':
    case 'BEAM_RESUME':
    case 'BEAM_COMPLETE': {
      if (a.beamNo != null) parts.push(`Beam #${a.beamNo}`);
      if (ctx.machineNo) parts.push(`Machine ${ctx.machineNo}`);
      if (a.eventType === 'BEAM_COMPLETE') {
        if (ctx.produced != null) parts.push(`${ctx.produced} produced`);
        if (ctx.scrapped != null && Number(ctx.scrapped) > 0) parts.push(`${ctx.scrapped} scrapped`);
      }
      return parts.length ? parts.join(' · ') : null;
    }
    case 'VENDOR_SEND':
    case 'VENDOR_RECEIVE': {
      if (ctx.stage) parts.push(workflowStatusLabel(ctx.stage));
      if (ctx.vendorPartyName) parts.push(String(ctx.vendorPartyName).trim());
      if (ctx.chalanNo) parts.push(`Chalan ${String(ctx.chalanNo).trim()}`);
      if (a.eventType === 'VENDOR_RECEIVE' && ctx.piecesLost != null && Number(ctx.piecesLost) > 0) {
        const lr = LOSS_REASON_LABEL[ctx.lossReason] || humanizeSnake(ctx.lossReason || '');
        parts.push(`${ctx.piecesLost} lost${lr ? ` (${lr})` : ''}`);
      }
      return parts.length ? parts.join(' · ') : null;
    }
    case 'DISPATCH': {
      if (canSeeCommercial && ctx.billNo) parts.push(`Bill ${String(ctx.billNo).trim()}`);
      if (ctx.chalanNo) parts.push(`Chalan ${String(ctx.chalanNo).trim()}`);
      if (ctx.damagedPieces != null && Number(ctx.damagedPieces) >= 0) {
        parts.push(`${ctx.damagedPieces} damaged`);
      }
      if (ctx.dispatchDate) parts.push(formatShortActivityDate(ctx.dispatchDate) || String(ctx.dispatchDate));
      return parts.length ? parts.join(' · ') : null;
    }
    case 'DELIVER': {
      if (ctx.deliveryDate) return formatShortActivityDate(ctx.deliveryDate) || String(ctx.deliveryDate);
      return null;
    }
    default:
      return null;
  }
}

// Immutable audit log, newest first.
export function OrderActivityTab({ activities, loading, canSeeCommercial = true }) {
  const [actTab, setActTab] = useState('all');

  const tabCounts = useMemo(() => {
    if (!activities || activities.length === 0) {
      return { all: 0, shop: 0, order: 0 };
    }
    let shop = 0;
    let order = 0;
    for (const a of activities) {
      if (isShopFloorEvent(a.eventType)) shop += 1;
      else order += 1;
    }
    return { all: activities.length, shop, order };
  }, [activities]);

  const visible = useMemo(() => {
    if (!activities) return [];
    if (actTab === 'all') return activities;
    if (actTab === 'shop') return activities.filter((x) => isShopFloorEvent(x.eventType));
    if (actTab === 'order') return activities.filter((x) => !isShopFloorEvent(x.eventType));
    return activities;
  }, [activities, actTab]);

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    );
  }
  if (!activities || activities.length === 0) {
    return (
      <EmptyState
        title="No activity yet"
        description="Every status change, beam start / complete and vendor dispatch will show up here."
      />
    );
  }
  return (
    <div className="space-y-4">
      <FilterSection title="Filter activity" hint="Shop floor vs office / delivery events.">
        <div className="space-y-2">
          <p className="text-xs font-medium text-slate-600">Event type</p>
          <FilterTabs
            value={actTab}
            onChange={setActTab}
            size="sm"
            tabs={[
              { id: 'all', label: 'All', count: tabCounts.all },
              { id: 'shop', label: 'Shop & vendors', count: tabCounts.shop },
              { id: 'order', label: 'Order & delivery', count: tabCounts.order },
            ]}
          />
        </div>
      </FilterSection>
      {visible.length === 0 ? (
        <p className="text-sm text-slate-500">Nothing in this group.</p>
      ) : (
        <ol className="relative space-y-3 border-l border-slate-200 pl-6">
          {visible.map((a) => {
            const meta = activityMetaLine(a, canSeeCommercial);
            return (
              <li key={a.id} className="relative">
                <span
                  className={[
                    'absolute -left-[29px] top-1.5 flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-white',
                    eventDot(a.eventType),
                  ].join(' ')}
                  aria-hidden="true"
                />
                <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900">{eventLabel(a)}</p>
                      {meta ? <p className="mt-1 text-xs text-slate-600">{meta}</p> : null}
                      {a.reason && a.eventType !== 'VENDOR_RECEIVE' ? (
                        <p className="mt-1 text-xs text-slate-500">
                          {a.eventType === 'HOLD' ? (
                            <>
                              Reason:{' '}
                              <span className="font-semibold text-slate-700">{formatReasonField(a.reason)}</span>
                            </>
                          ) : (
                            <span className="font-semibold text-slate-700">{formatReasonField(a.reason)}</span>
                          )}
                        </p>
                      ) : null}
                      {a.note ? <p className="mt-1 text-xs text-slate-600">{a.note}</p> : null}
                    </div>
                    <div className="shrink-0 text-right text-[11px] text-slate-400">
                      <div className="font-semibold text-slate-600">{a.actorUserName || 'System'}</div>
                      <div>{formatDateTime(a.createdAt)}</div>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function formatReasonField(reason) {
  if (!reason) return '—';
  const s = String(reason).trim();
  return LOSS_REASON_LABEL[s] || HOLD_REASON_LABEL[s] || humanizeSnake(s);
}

function eventDot(type) {
  switch (type) {
    case 'ORDER_STATUS_CHANGE':
      return 'bg-primary-500';
    case 'APPROVE':
      return 'bg-blue-500';
    case 'HOLD':
      return 'bg-amber-500';
    case 'RELEASE':
      return 'bg-emerald-500';
    case 'BEAM_START':
      return 'bg-teal-500';
    case 'BEAM_HOLD':
      return 'bg-amber-500';
    case 'BEAM_RESUME':
      return 'bg-teal-500';
    case 'BEAM_COMPLETE':
      return 'bg-emerald-500';
    case 'VENDOR_SEND':
      return 'bg-indigo-500';
    case 'VENDOR_RECEIVE':
      return 'bg-violet-500';
    case 'DISPATCH':
      return 'bg-emerald-500';
    case 'DELIVER':
      return 'bg-emerald-600';
    case 'CANCEL':
      return 'bg-rose-600';
    case 'NOTE':
      return 'bg-slate-500';
    default:
      return 'bg-slate-400';
  }
}

/** Short title; beam # / machine / vendor sit on the meta line below. */
function eventLabel(a) {
  switch (a.eventType) {
    case 'ORDER_STATUS_CHANGE':
      return `Order moved ${workflowStatusLabel(a.fromStatus)} → ${workflowStatusLabel(a.toStatus)}`;
    case 'APPROVE':
      return 'Order approved';
    case 'HOLD':
      return 'Order placed on hold';
    case 'RELEASE':
      return 'Hold released';
    case 'BEAM_START':
      return 'Beam started';
    case 'BEAM_HOLD':
      return 'Beam paused';
    case 'BEAM_RESUME':
      return 'Beam resumed';
    case 'BEAM_COMPLETE':
      return 'Beam completed';
    case 'VENDOR_SEND':
      return 'Sent to vendor';
    case 'VENDOR_RECEIVE':
      return 'Received from vendor';
    case 'DISPATCH':
      return 'Order dispatched';
    case 'DELIVER':
      return 'Order delivered';
    case 'CANCEL':
      return 'Order cancelled';
    case 'NOTE':
      return 'Note added';
    default:
      return humanizeSnake(String(a.eventType || 'Event'));
  }
}

function formatDateTime(s) {
  if (!s) return '';
  try {
    const d = new Date(s);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return s;
  }
}
